import { clearOtherAdvancedStyles } from "@blocks/utils";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { TextareaControl } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import React, { useMemo } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import LayoutOptionsRenderer from "../../../components/common/LayoutOptionsRenderer";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	HeadingLayout1Style1,
	HeadingLayout1Style2,
	HeadingLayout2Style1,
	HeadingLayout3Style1,
	HeadingLayout4Style1,
	HeadingLayout5Style1,
	HeadingLayout6Style1,
	HeadingLayout7Style1,
	HeadingLayout8Style1,
	HeadingLayout9Style1,
	TextAlignCenter,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			enableSubHeading,
			label,
			subHeadingColor,
			subHeadingHoverColor,
			subHeadingBackground,
			subHeadingHoverBackground,
			subHeadingPadding,
			subHeadingBorder,
			subHeadingBoxShadow,
			subHeadingHoverBorder,
			subHeadingBoxShadowHover,

			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
			headingLayout,

			dotColor,
			headingBorder,
			headingBorderBefore,
			headingBorderAfter,
			headingBorderBottomInset,
			headingPadding,

			blockPadding,
			blockMargin,
			alignment,
			size,
			borderBottomColorHover,
			sizeHover,
			color,
			borderBottomColor,
			margin,
			markup,
			background,
			hoverColor,
			hoverBackground,
			typography,
			subHeadingTypography,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			lineAccent,
			lineColor,
		},
		setAttributes,
	} = props;

	const headingLayoutsData = useMemo(
		() => [
			{
				id: "heading-layout-1",
				title: __("Layout 1", "magazine-blocks"),
				attrKey: "headingLayout1AdvancedStyle",
				value: headingLayout1AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-1-style-1",
						icon: HeadingLayout1Style1,
					},
					{
						label: __("Style 2", "magazine-blocks"),
						value: "heading-layout-1-style-2",
						icon: HeadingLayout1Style2,
					},
				],
			},
			{
				id: "heading-layout-2",
				title: __("Layout 2", "magazine-blocks"),
				attrKey: "headingLayout2AdvancedStyle",
				value: headingLayout2AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-2-style-1",
						icon: HeadingLayout2Style1,
					},
				],
			},
			{
				id: "heading-layout-3",
				title: __("Layout 3", "magazine-blocks"),
				attrKey: "headingLayout3AdvancedStyle",
				value: headingLayout3AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-3-style-1",
						icon: HeadingLayout3Style1,
					},
				],
			},
			{
				id: "heading-layout-4",
				title: __("Layout 4", "magazine-blocks"),
				attrKey: "headingLayout4AdvancedStyle",
				value: headingLayout4AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-4-style-1",
						icon: HeadingLayout4Style1,
					},
				],
			},
			{
				id: "heading-layout-5",
				title: __("Layout 5", "magazine-blocks"),
				attrKey: "headingLayout5AdvancedStyle",
				value: headingLayout5AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-5-style-1",
						icon: HeadingLayout5Style1,
					},
				],
			},
			{
				id: "heading-layout-6",
				title: __("Layout 6", "magazine-blocks"),
				attrKey: "headingLayout6AdvancedStyle",
				value: headingLayout6AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-6-style-1",
						icon: HeadingLayout6Style1,
					},
				],
			},
			{
				id: "heading-layout-7",
				title: __("Layout 7", "magazine-blocks"),
				attrKey: "headingLayout7AdvancedStyle",
				value: headingLayout7AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-7-style-1",
						icon: HeadingLayout7Style1,
					},
				],
			},
			{
				id: "heading-layout-8",
				title: __("Layout 8", "magazine-blocks"),
				attrKey: "headingLayout8AdvancedStyle",
				value: headingLayout8AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-8-style-1",
						icon: HeadingLayout8Style1,
					},
				],
			},
			{
				id: "heading-layout-9",
				title: __("Layout 9", "magazine-blocks"),
				attrKey: "headingLayout9AdvancedStyle",
				value: headingLayout9AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-9-style-1",
						icon: HeadingLayout9Style1,
					},
				],
			},
		],
		[
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
		]
	);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Text Alignment", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<TextAlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<TextAlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<TextAlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
					<LayoutOptionsRenderer
						columns={2}
						layoutsData={headingLayoutsData}
						selectedValues={Object.fromEntries(
							headingLayoutsData.map((ld) => [
								ld.attrKey,
								ld.value,
							])
						)}
						onChange={(attrKey, value, layoutId) =>
							setAttributes({
								...clearOtherAdvancedStyles(
									headingLayoutsData,
									attrKey
								),
								[attrKey]: value,
								headingLayout: layoutId,
							})
						}
						currentLayout={headingLayout}
					/>
					<Select
						options={[
							{
								label: __("H1", "magazine-blocks"),
								value: "h1",
							},
							{
								label: __("H2", "magazine-blocks"),
								value: "h2",
							},
							{
								label: __("H3", "magazine-blocks"),
								value: "h3",
							},
							{
								label: __("H4", "magazine-blocks"),
								value: "h4",
							},
							{
								label: __("H5", "magazine-blocks"),
								value: "h5",
							},
							{
								label: __("H6", "magazine-blocks"),
								value: "h6",
							},
						]}
						onChange={(val) => setAttributes({ markup: val })}
						value={markup}
						label={__("HTML Markup", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Sub Heading", "magazine-blocks")}>
					<Toggle
						checked={enableSubHeading}
						onChange={() =>
							setAttributes({
								enableSubHeading: !enableSubHeading,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableSubHeading && (
						<TextareaControl
							onChange={(val) => setAttributes({ label: val })}
							label={__("Text", "magazine-blocks")}
							value={label || ""}
						/>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Heading", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: color, id: "color" },
										{ color: hoverColor, id: "hoverColor" },
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={hoverColor}
										resetKey="hoverColor"
										onReset={(v) => {
											setAttributes({ hoverColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={hoverColor}
										onChange={(v) =>
											setAttributes({ hoverColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					{"heading-layout-2" === headingLayout && (
						<PopoverDrawer
							label={__("Background Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: background,
												id: "background",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={background}
											resetKey="background"
											onReset={(v) => {
												setAttributes({
													background: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={background}
											onChange={(v) =>
												setAttributes({ background: v })
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
					)}
					<Typography
						value={typography}
						onChange={(val) => setAttributes({ typography: val })}
						resetAttributeKey="typography"
					/>
					{("heading-layout-4" === headingLayout ||
						"heading-layout-5" === headingLayout) && (
						<PopoverDrawer
							label={__("Dot Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: dotColor,
												id: "dotColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={dotColor}
											resetKey="dotColor"
											onReset={(v) => {
												setAttributes({ dotColor: v });
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={dotColor}
											onChange={(v) =>
												setAttributes({ dotColor: v })
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
					)}

					{("heading-layout-1" === headingLayout ||
						"heading-layout-3" === headingLayout ||
						"heading-layout-8" === headingLayout) && (
						<>
							<PopoverDrawer
								label={__("Line Accent", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: lineAccent,
													id: "lineAccent",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={lineAccent}
												resetKey="lineAccent"
												onReset={(v) => {
													setAttributes({
														lineAccent: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={lineAccent}
												onChange={(v) =>
													setAttributes({
														lineAccent: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						</>
					)}

					{("heading-layout-2" === headingLayout ||
						"heading-layout-3" === headingLayout ||
						"heading-layout-6" === headingLayout ||
						"heading-layout-7" === headingLayout ||
						"heading-layout-9" === headingLayout) && (
						<>
							<PopoverDrawer
								label={__("Line Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: lineColor,
													id: "lineColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={lineColor}
												resetKey="lineColor"
												onReset={(v) => {
													setAttributes({
														lineColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={lineColor}
												onChange={(v) =>
													setAttributes({
														lineColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						</>
					)}
					<Dimensions
						value={headingPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({
								headingPadding: val,
							})
						}
						resetAttributeKey="headingPadding"
					/>
					<Dimensions
						value={margin}
						responsive
						label={__("Margin", "magazine-blocks")}
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) => setAttributes({ margin: val })}
						type="margin"
						resetAttributeKey="margin"
					/>
					<Tabs position="relative">
						<TabList>
							<Tab>{__("Normal", "magazine-blocks")}</Tab>
							{(("heading-layout-1" === headingLayout &&
								"heading-layout-1-style-1" ===
									headingLayout1AdvancedStyle) ||
								("heading-layout-3" === headingLayout &&
									"heading-layout-3-style-1" ===
										headingLayout3AdvancedStyle) ||
								("heading-layout-6" === headingLayout &&
									"heading-layout-6-style-1" ===
										headingLayout6AdvancedStyle) ||
								("heading-layout-8" === headingLayout &&
									"heading-layout-8-style-1" ===
										headingLayout8AdvancedStyle)) && (
								<Tab>{__("Before", "magazine-blocks")}</Tab>
							)}
							{(("heading-layout-6" === headingLayout &&
								"heading-layout-6-style-1" ===
									headingLayout6AdvancedStyle) ||
								("heading-layout-7" === headingLayout &&
									"heading-layout-7-style-1" ===
										headingLayout7AdvancedStyle)) && (
								<Tab>{__("After", "magazine-blocks")}</Tab>
							)}
						</TabList>
						<TabPanels>
							<TabPanel paddingBottom="0">
								<Border
									value={headingBorder}
									onChange={(v) => {
										setAttributes({
											headingBorder: v,
										});
									}}
									resetAttributeKey="headingBorder"
								/>
							</TabPanel>
							{(("heading-layout-1" === headingLayout &&
								"heading-layout-1-style-1" ===
									headingLayout1AdvancedStyle) ||
								("heading-layout-3" === headingLayout &&
									"heading-layout-3-style-1" ===
										headingLayout3AdvancedStyle) ||
								("heading-layout-6" === headingLayout &&
									"heading-layout-6-style-1" ===
										headingLayout6AdvancedStyle) ||
								("heading-layout-8" === headingLayout &&
									"heading-layout-8-style-1" ===
										headingLayout8AdvancedStyle)) && (
								<TabPanel paddingBottom="0">
									<Border
										value={headingBorderBefore}
										onChange={(v) => {
											setAttributes({
												headingBorderBefore: v,
											});
										}}
										resetAttributeKey="headingBorderBefore"
									/>
									{"heading-layout-3" === headingLayout &&
										"heading-layout-3-style-1" ===
											headingLayout3AdvancedStyle && (
											<Slider
												onChange={(val: any) =>
													setAttributes({
														headingBorderBottomInset:
															val,
													})
												}
												label={__(
													"Border Bottom Inset",
													"magazine-blocks"
												)}
												units={["px", "em", "%"]}
												min={-100}
												max={100}
												value={headingBorderBottomInset}
												resetAttributeKey="headingBorderBottomInset"
											/>
										)}
								</TabPanel>
							)}
							{(("heading-layout-6" === headingLayout &&
								"heading-layout-6-style-1" ===
									headingLayout6AdvancedStyle) ||
								("heading-layout-7" === headingLayout &&
									"heading-layout-7-style-1" ===
										headingLayout7AdvancedStyle)) && (
								<TabPanel paddingBottom="0">
									<Border
										value={headingBorderAfter}
										onChange={(v) => {
											setAttributes({
												headingBorderAfter: v,
											});
										}}
										resetAttributeKey="headingBorderAfter"
									/>
								</TabPanel>
							)}
						</TabPanels>
					</Tabs>
				</Panel>
				{enableSubHeading && (
					<Panel title={__("Sub Heading", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: subHeadingColor,
												id: "categoryTitleColor",
											},
											{
												color: subHeadingHoverColor,
												id: "categoryTitleHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={subHeadingColor}
											resetKey="subHeadingColor"
											onReset={(v) => {
												setAttributes({
													subHeadingColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={subHeadingColor}
											onChange={(v) =>
												setAttributes({
													subHeadingColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={subHeadingHoverColor}
											resetKey="subHeadingHoverColor"
											onReset={(v) => {
												setAttributes({
													subHeadingHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={subHeadingHoverColor}
											onChange={(v) =>
												setAttributes({
													subHeadingHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background Color", "magazine-blocks")}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={subHeadingBackground}
											resetKey="subHeadingBackground"
											onReset={(v) => {
												setAttributes({
													subHeadingBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={subHeadingBackground}
											onChange={(v) =>
												setAttributes({
													subHeadingBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={subHeadingHoverBackground}
											resetKey="countHoverBackground"
											onReset={(v) => {
												setAttributes({
													subHeadingHoverBackground:
														v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={subHeadingHoverBackground}
											onChange={(v) =>
												setAttributes({
													subHeadingHoverBackground:
														v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Typography
							value={subHeadingTypography}
							onChange={(val) =>
								setAttributes({ subHeadingTypography: val })
							}
							resetAttributeKey="subHeadingTypography"
						/>
						{enableSubHeading && (
							<Dimensions
								value={subHeadingPadding}
								responsive
								label={__("Padding", "magazine-blocks")}
								type="padding"
								defaultUnit="px"
								units={["px", "rem", "em", "%"]}
								onChange={(val) =>
									setAttributes({ subHeadingPadding: val })
								}
								resetAttributeKey="subHeadingPadding"
							/>
						)}
						<Tabs isFitted variant="unstyled">
							<TabList
								border="1px solid #E0E0E0"
								p="4px"
								borderRadius="2px"
							>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Normal
								</Tab>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Hover
								</Tab>
							</TabList>
							<TabPanels>
								<TabPanel p="16px 0">
									<Border
										value={subHeadingBorder}
										onChange={(v) => {
											setAttributes({
												subHeadingBorder: v,
											});
										}}
										resetAttributeKey="subHeadingBorder"
									/>
									<BoxShadow
										resetAttributeKey="subHeadingBoxShadow"
										value={subHeadingBoxShadow}
										onChange={(val) =>
											setAttributes({
												subHeadingBoxShadow: val,
											})
										}
									/>
								</TabPanel>
								<TabPanel p="16px 0">
									<Border
										value={subHeadingHoverBorder}
										onChange={(v) => {
											setAttributes({
												subHeadingHoverBorder: v,
											});
										}}
										resetAttributeKey="subHeadingHoverBorder"
									/>
									<BoxShadow
										resetAttributeKey="subHeadingBoxShadowHover"
										value={subHeadingBoxShadowHover}
										onChange={(val) =>
											setAttributes({
												subHeadingBoxShadowHover: val,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</Panel>
				)}
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
